<script lang="ts">
    import AppLayout from '@/layouts/AppLayout.svelte';
    import { type BreadcrumbItem } from '@/types';
    import * as Card from '@/components/ui/card';
    import { Button } from '@/components/ui/button';
    import { Badge } from '@/components/ui/badge';
    import { Textarea } from '@/components/ui/textarea';
    import { router, useForm } from '@inertiajs/svelte';
    import { toast } from 'svelte-sonner';
    import {
        CheckCircle2,
        Circle,
        Clock,
        ArrowLeft,
        Trash,
        User,
        Calendar,
        MessageSquare,
        Send,
        ExternalLink
    } from 'lucide-svelte';

    type UserType = {
        id: string;
        name: string;
    };

    type Comment = {
        id: string;
        comment: string;
        created_at: string;
        user: UserType;
    };

    type Task = {
        id: string;
        title: string;
        description: string | null;
        priority: 'low' | 'medium' | 'high';
        status: 'pending' | 'in_progress' | 'completed';
        due_date: string | null;
        completed_at: string | null;
        created_at: string;
        creator: { name: string };
        assignee?: UserType;
        related_record_url?: string | null;
        related_record_label?: string | null;
        related_record_type_name?: string | null;
        comments: Comment[];
    };

    type Props = {
        task: Task;
    };

    let { task }: Props = $props();

    const breadcrumbs: BreadcrumbItem[] = [
        { title: 'Activity', href: '/activity' },
        { title: task.title, href: `/tasks/${task.id}` },
    ];

    function getPriorityColor(priority: string) {
        switch (priority) {
            case 'high': return 'destructive';
            case 'medium': return 'default';
            case 'low': return 'secondary';
            default: return 'default';
        }
    }

    function getStatusIcon(status: string) {
        switch (status) {
            case 'completed': return CheckCircle2;
            case 'in_progress': return Clock;
            default: return Circle;
        }
    }

    function formatDate(dateString: string | null) {
        if (!dateString) return 'Not set';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function goBack() {
        router.visit('/activity');
    }

    function deleteTask() {
        if (confirm('Are you sure you want to delete this task?')) {
            router.delete(`/tasks/${task.id}`);
        }
    }

    function updateStatus(newStatus: string) {
        router.put(`/tasks/${task.id}`, { status: newStatus }, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Task marked as ${newStatus.replace('_', ' ')}`);
            }
        });
    }

    // Comment form
    let commentForm = useForm({
        comment: ''
    });

    function submitComment() {
        $commentForm.post(`/tasks/${task.id}/comments`, {
            preserveScroll: true,
            onSuccess: () => {
                $commentForm.reset();
                toast.success('Comment added');
            }
        });
    }

    const StatusIcon = $derived(getStatusIcon(task.status));
</script>

<svelte:head>
    <title>{task.title} - Task</title>
</svelte:head>

<AppLayout {breadcrumbs}>
    <div class="p-4 max-w-4xl mx-auto space-y-4">
        <!-- Header Actions -->
        <div class="flex items-center justify-between">
            <Button variant="ghost" size="sm" onclick={goBack}>
                <ArrowLeft class="h-4 w-4 mr-2" />
                Back to Activity
            </Button>
            <div class="flex gap-2">
                <Button variant="destructive" size="sm" onclick={deleteTask}>
                    <Trash class="h-4 w-4 mr-2" />
                    Delete
                </Button>
            </div>
        </div>

        <!-- Main Task Card -->
        <Card.Root>
            <Card.Header>
                <div class="flex items-start gap-4">
                    <div class="mt-1">
                        <StatusIcon class="h-6 w-6" style="color: var({task.status === 'completed' ? '--activity-complete' : '--error'});" />
                    </div>
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-2">
                            <Badge variant={getPriorityColor(task.priority)}>
                                {task.priority} priority
                            </Badge>
                            <Badge
                                variant="secondary"
                                style="background-color: var({task.status === 'completed' ? '--activity-complete' : '--error'}); color: white;"
                            >
                                {task.status.replace('_', ' ')}
                            </Badge>
                        </div>
                        <Card.Title class="text-xl">{task.title}</Card.Title>
                    </div>
                </div>
            </Card.Header>
            <Card.Content class="space-y-4">
                <!-- Description -->
                {#if task.description}
                    <div class="prose prose-sm max-w-none">
                        <p class="whitespace-pre-wrap">{task.description}</p>
                    </div>
                {:else}
                    <p class="text-muted-foreground text-sm">No description provided</p>
                {/if}

                <!-- Status Actions -->
                <div class="border-t pt-4">
                    <p class="text-sm font-medium mb-2">Update Status:</p>
                    <div class="flex gap-2">
                        <Button
                            variant={task.status === 'pending' ? 'default' : 'outline'}
                            size="sm"
                            onclick={() => updateStatus('pending')}
                        >
                            <Circle class="h-4 w-4 mr-2" />
                            Pending
                        </Button>
                        <Button
                            variant={task.status === 'in_progress' ? 'default' : 'outline'}
                            size="sm"
                            onclick={() => updateStatus('in_progress')}
                        >
                            <Clock class="h-4 w-4 mr-2" />
                            In Progress
                        </Button>
                        <Button
                            variant={task.status === 'completed' ? 'default' : 'outline'}
                            size="sm"
                            onclick={() => updateStatus('completed')}
                        >
                            <CheckCircle2 class="h-4 w-4 mr-2" />
                            Completed
                        </Button>
                    </div>
                </div>

                <!-- Meta Information -->
                <div class="border-t pt-4 space-y-2 text-sm text-muted-foreground">
                    <div class="flex items-center gap-2">
                        <User class="h-4 w-4" />
                        <span>Created by: <strong class="text-foreground">{task.creator.name}</strong></span>
                    </div>
                    {#if task.assignee}
                        <div class="flex items-center gap-2">
                            <User class="h-4 w-4" />
                            <span>Assigned to: <strong class="text-foreground">{task.assignee.name}</strong></span>
                        </div>
                    {/if}
                    <div class="flex items-center gap-2">
                        <Calendar class="h-4 w-4" />
                        <span>Created: {formatDate(task.created_at)}</span>
                    </div>
                    {#if task.due_date}
                        <div class="flex items-center gap-2">
                            <Clock class="h-4 w-4" />
                            <span>Due: <strong class="text-foreground">{formatDate(task.due_date)}</strong></span>
                        </div>
                    {/if}
                    {#if task.completed_at}
                        <div class="flex items-center gap-2">
                            <CheckCircle2 class="h-4 w-4" style="color: var(--activity-complete);" />
                            <span>Completed: {formatDate(task.completed_at)}</span>
                        </div>
                    {/if}
                </div>

                <!-- Related Record -->
                {#if task.related_record_label}
                    <div class="border-t pt-4">
                        <div class="flex items-center gap-2 text-sm text-muted-foreground">
                            <ExternalLink class="h-4 w-4" />
                            <span>Related to:</span>
                            {#if task.related_record_url}
                                <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- URL pre-resolved from backend -->
                                <a href={task.related_record_url}
                                    class="text-primary hover:underline font-medium"
                                >
                                    {#if task.related_record_type_name}
                                        <span>{task.related_record_type_name}:</span>
                                    {/if}
                                    {task.related_record_label}
                                </a>
                            {:else}
                                <strong class="text-foreground">
                                    {#if task.related_record_type_name}{task.related_record_type_name}: {/if}{task.related_record_label}
                                </strong>
                            {/if}
                        </div>
                    </div>
                {/if}
            </Card.Content>
        </Card.Root>

        <!-- Comments Section -->
        <Card.Root>
            <Card.Header>
                <Card.Title class="flex items-center gap-2">
                    <MessageSquare class="h-5 w-5" />
                    Comments ({task.comments?.length || 0})
                </Card.Title>
            </Card.Header>
            <Card.Content class="space-y-4">
                <!-- Existing Comments -->
                {#if task.comments && task.comments.length > 0}
                    <div class="space-y-3">
                        {#each task.comments as comment (comment.id)}
                            <div class="border rounded-lg p-3">
                                <div class="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                                    <User class="h-4 w-4" />
                                    <strong class="text-foreground">{comment.user.name}</strong>
                                    <span>&bull;</span>
                                    <span>{formatDate(comment.created_at)}</span>
                                </div>
                                <p class="text-sm whitespace-pre-wrap">{comment.comment}</p>
                            </div>
                        {/each}
                    </div>
                {:else}
                    <p class="text-sm text-muted-foreground text-center py-4">No comments yet</p>
                {/if}

                <!-- Add Comment Form -->
                <form onsubmit={(e) => { e.preventDefault(); submitComment(); }} class="space-y-3">
                    <Textarea
                        bind:value={$commentForm.comment}
                        placeholder="Add a comment..."
                        rows={3}
                    />
                    <Button
                        type="submit"
                        size="sm"
                        disabled={!$commentForm.comment || $commentForm.processing}
                    >
                        <Send class="h-4 w-4 mr-2" />
                        Add Comment
                    </Button>
                </form>
            </Card.Content>
        </Card.Root>
    </div>
</AppLayout>